/**
 * GRNDetail — full audit view, Post action (irreversible) for draft GRNs.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  Package, CheckCircle2, AlertTriangle, Trash2, ArrowUpRightFromSquare,
  Clock, FileText, Building2, Calendar, Warehouse, Download,
} from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../components/ui/card';
import BackButton from '../../../../components/admin/BackButton';
import { useToast } from '../../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  partial: { label: 'Partial', color: 'bg-amber-100 text-amber-800' },
  received: { label: 'Received (Complete)', color: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800' },
};

const GRNDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);

  const fetchGrn = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/procurement/grns/${id}`, auth);
      setGrn(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Error', description: e.response?.data?.detail || 'Failed to load GRN.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGrn(); }, [id]); // eslint-disable-line

  const onPost = async () => {
    if (!window.confirm(`Post GRN ${grn.grn_number}?\n\nThis is IRREVERSIBLE and will:\n• Create stock movements\n• Update the PO delivery status\n• Lock the GRN from further edits`)) return;
    setPosting(true);
    try {
      await axios.post(`${API_URL}/api/procurement/grns/${id}/post`, {}, auth);
      toast({ title: 'GRN posted', description: `${grn.grn_number} is now final.` });
      fetchGrn();
    } catch (e) {
      toast({ variant: 'destructive', title: 'Post failed', description: e.response?.data?.detail || e.message });
    } finally {
      setPosting(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete draft GRN ${grn.grn_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/procurement/grns/${id}`, auth);
      toast({ title: 'GRN deleted' });
      navigate('/admin/procurement/grns');
    } catch (e) {
      toast({ variant: 'destructive', title: 'Delete failed', description: e.response?.data?.detail || e.message });
    }
  };

  if (loading) return <div className="p-6 text-center text-[#6C757D]">Loading…</div>;
  if (!grn) return <div className="p-6">Not found.</div>;
  const meta = STATUS_META[grn.status] || STATUS_META.draft;
  const isDraft = grn.status === 'draft';

  return (
    <div className="p-6 space-y-5" data-testid="grn-detail-page">
      <BackButton />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
            <Package className="w-6 h-6 text-[#023E8A]" /> {grn.grn_number}
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs px-2 py-0.5 rounded ${meta.color}`}>{meta.label}</span>
            <span className="text-xs text-[#6C757D]">Created {grn.created_at?.slice(0, 10)}</span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => window.open(`${API_URL}/api/procurement/grns/${id}/pdf?token=${encodeURIComponent(token)}`, '_blank')}
            className="border-[#023E8A] text-[#023E8A] hover:bg-blue-50"
            data-testid="grn-download-pdf"
          >
            <Download className="w-4 h-4 mr-1.5" /> PDF
          </Button>
          {isDraft && (
            <>
              <Button variant="outline" onClick={onDelete} className="text-red-600 border-red-200" data-testid="grn-delete">
                <Trash2 className="w-4 h-4 mr-1.5" /> Delete
              </Button>
              <Button onClick={onPost} disabled={posting} className="bg-green-600 hover:bg-green-700 text-white" data-testid="grn-post-btn">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> {posting ? 'Posting…' : 'Post GRN (irreversible)'}
              </Button>
            </>
          )}
          <Button
            variant="outline"
            onClick={() => window.open(`/admin/procurement/purchase-orders/${grn.po_id}`, '_blank')}
            data-testid="grn-open-po"
          >
            <ArrowUpRightFromSquare className="w-4 h-4 mr-1.5" /> Open linked PO
          </Button>
        </div>
      </div>

      {/* Timeline */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Timeline</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-4 text-sm">
          <TimelineStep label="Created" date={grn.created_at} icon={<Clock />} color="text-gray-600" />
          <TimelineStep label="Received on site" date={grn.received_date} icon={<Package />} color="text-blue-600" />
          <TimelineStep label="Posted" date={grn.posted_at} icon={<CheckCircle2 />} color="text-green-600" pending={!grn.posted_at} />
        </CardContent>
      </Card>

      {/* Snapshots */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <SnapshotCard title="Issuer (Our company)" icon={<Building2 />} data={[
          ['Company', grn.company_snapshot?.trading_name || grn.company_snapshot?.legal_name],
          ['Country', grn.company_snapshot?.country],
          ['TRN', grn.company_snapshot?.trn],
        ]} />
        <SnapshotCard title="Supplier" icon={<Building2 />} data={[
          ['Name', grn.supplier_snapshot?.company_name],
          ['Contact', grn.supplier_snapshot?.contact_name],
          ['Email', grn.supplier_snapshot?.email],
          ['Phone', grn.supplier_snapshot?.phone],
        ]} />
        <SnapshotCard title="Purchase Order" icon={<FileText />} data={[
          ['PO #', grn.po_snapshot?.po_number],
          ['PO date', grn.po_snapshot?.po_date?.slice(0, 10)],
          ['Expected', grn.po_snapshot?.expected_delivery_date?.slice(0, 10)],
          ['Currency', grn.po_snapshot?.currency],
        ]} />
      </div>

      {/* Receipt metadata */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Info label="Received date" value={grn.received_date?.slice(0, 10)} icon={<Calendar />} />
        <Info label="Warehouse" value={grn.warehouse_id || '—'} icon={<Warehouse />} />
        <Info label="Tolerance" value={`${grn.tolerance_pct}%`} />
        <Info label="Stock movements" value={grn.stock_movement_ids?.length || 0} />
      </div>

      {grn.notes && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Notes</CardTitle></CardHeader>
          <CardContent><div className="text-sm whitespace-pre-wrap text-[#2B2D42]">{grn.notes}</div></CardContent>
        </Card>
      )}

      {/* Items */}
      <Card>
        <CardHeader><CardTitle>Line items</CardTitle></CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="grn-detail-items">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-right px-3 py-2">Ordered</th>
                <th className="text-right px-3 py-2">Prev. received</th>
                <th className="text-right px-3 py-2">This GRN</th>
                <th className="text-right px-3 py-2">Pending after</th>
                <th className="text-right px-3 py-2">Rejected</th>
                <th className="text-left px-3 py-2">Inspection</th>
              </tr>
            </thead>
            <tbody>
              {grn.items.map((it, idx) => {
                const pendingAfter = (it.qty_ordered || 0) - (it.qty_previously_received || 0) - (it.qty_received || 0);
                const hasDisc = pendingAfter !== 0;
                return (
                  <tr key={idx} className="border-b" data-testid={`grn-detail-item-${idx}`}>
                    <td className="px-3 py-2 font-medium">{it.product_name}</td>
                    <td className="text-right px-3 py-2">{it.qty_ordered}</td>
                    <td className="text-right px-3 py-2">{it.qty_previously_received}</td>
                    <td className="text-right px-3 py-2 font-semibold text-[#023E8A]">{it.qty_received}</td>
                    <td className={`text-right px-3 py-2 ${hasDisc ? 'text-amber-700 font-semibold' : ''}`}>
                      {pendingAfter}
                      {hasDisc && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                    </td>
                    <td className="text-right px-3 py-2">{it.rejected_quantity || 0}</td>
                    <td className="px-3 py-2 text-xs text-[#6C757D]">{it.inspection_notes || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

const TimelineStep = ({ label, date, icon, color = 'text-gray-600', pending }) => (
  <div className={`flex items-center gap-2 ${pending ? 'opacity-40' : ''}`}>
    <span className={`w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center ${color}`}>
      {React.cloneElement(icon, { className: 'w-3.5 h-3.5' })}
    </span>
    <div>
      <div className="text-xs text-[#6C757D]">{label}</div>
      <div className="text-sm font-medium">{date ? date.slice(0, 19).replace('T', ' ') : 'Pending'}</div>
    </div>
  </div>
);

const SnapshotCard = ({ title, icon, data }) => (
  <Card>
    <CardHeader className="pb-1">
      <CardTitle className="text-xs text-[#6C757D] uppercase tracking-wide flex items-center gap-1">
        {React.cloneElement(icon, { className: 'w-3 h-3' })} {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="text-sm space-y-0.5">
      {data.filter(([, v]) => v).map(([k, v]) => (
        <div key={k}>
          <span className="text-[#6C757D] text-xs">{k}: </span>
          <span className="font-medium text-[#2B2D42]">{v}</span>
        </div>
      ))}
    </CardContent>
  </Card>
);

const Info = ({ label, value, icon }) => (
  <div className="bg-white border rounded-lg p-2.5">
    <div className="text-[10px] text-[#6C757D] uppercase tracking-wide inline-flex items-center gap-1">
      {icon && React.cloneElement(icon, { className: 'w-3 h-3' })}
      {label}
    </div>
    <div className="font-bold text-[#2B2D42] mt-0.5">{value}</div>
  </div>
);

export default GRNDetail;
